// WebAudio 共有 AudioContext。SE 用。
// iOS Safari の autoplay policy 対策で、ユーザー操作直下で resume する必要がある。

interface WebkitWindow extends Window {
  webkitAudioContext?: typeof AudioContext
}

let sharedCtx: AudioContext | null = null
let initFailed = false

export function getAudioContext(): AudioContext | null {
  if (sharedCtx) return sharedCtx
  if (initFailed) return null

  const win = window as WebkitWindow
  const AudioCtxClass = window.AudioContext ?? win.webkitAudioContext
  if (!AudioCtxClass) {
    initFailed = true
    // fallback:ok — Web Audio API 未対応環境では SE を無音扱いでゲーム継続
    return null
  }
  try {
    sharedCtx = new AudioCtxClass()
    // ページアンロード時に明示的に close する。iOS Safari は AudioContext の
    // 同時生成数に上限があり、SPA 遷移 / ホットリロード / LIFF navigateToLiffUrl
    // で同一プロセスがリソースを保持し続けると次回起動で生成が失敗する。
    // listener は 1 度だけ登録（initFailed か sharedCtx 確定後の経路では再到達しない）。
    if (typeof window !== 'undefined') {
      const onUnload = () => {
        if (sharedCtx && sharedCtx.state !== 'closed') {
          sharedCtx.close().catch(() => {
            // fallback:ok — アンロード中のため無視
          })
        }
      }
      window.addEventListener('beforeunload', onUnload, { once: true })
    }
  } catch {
    // fallback:ok — コンテキスト生成失敗は音声なしで継続
    initFailed = true
    sharedCtx = null
  }
  return sharedCtx
}

// ユーザー操作直下で呼ぶ。suspended の場合のみ resume を試みる。
export function resumeAudioContext(): void {
  const ctx = getAudioContext()
  if (!ctx) return
  if (ctx.state !== 'suspended') return
  ctx.resume().catch(() => {
    // fallback:ok — user gesture が無ければ次のユーザー操作で再試行される
  })
}

// ---------------------------------------------------------------------------
// 広告表示中の AudioContext 制御
//
// iOS の AVAudioSession は、AdMob 動画 / WebView の WebAudio が同時に音声を
// 出そうとすると競合する。広告表示前に AudioContext を suspend し、終了後に
// resume することで、SE の二重再生・音割れ・ミックスバグを抑える。
// `services/ads.ts:showRewardedAd` が表示前後で呼ぶ前提。
// ---------------------------------------------------------------------------

let adSuspended = false

export async function suspendForAd(): Promise<void> {
  adSuspended = true
  const ctx = sharedCtx
  if (!ctx) return
  if (ctx.state !== 'running') return
  try {
    await ctx.suspend()
  } catch (err) {
    // fallback:ok — suspend 失敗時も BGM ダッキング（setAdMuted）は別経路で
    // 動くため致命的ではない。ログのみ残す。
    console.warn('[audioContext] suspendForAd failed:', err)
  }
}

export async function resumeFromAd(): Promise<void> {
  adSuspended = false
  const ctx = getAudioContext()
  if (!ctx) return
  if (ctx.state === 'closed') return
  if (ctx.state === 'running') return
  try {
    await ctx.resume()
  } catch (err) {
    // fallback:ok — 次回ユーザー操作で resumeAudioContext() が再試行する。
    console.warn('[audioContext] resumeFromAd failed:', err)
  }
}

export function isAdSuspended(): boolean {
  return adSuspended
}
